/**
 * Reine URL-Builder für die Dienste, die am Immobilien-Risiko verlinkt sind:
 * Google Earth (3D-Schrägansicht), TIM-online NRW (ALKIS-Liegenschaftskarte)
 * und der Hochwasser-Check des GDV (T-339, nur an den Immobilien-Stellen). Bewusst ohne React — damit testbar und an allen
 * Stellen identisch (heute: `risikoImmobilieStep/AnschriftBlock`,
 * `immobilieContractStep/AllgemeinBlocks` und der Karten-Pin an jeder
 * Kontakt-Anschrift, `forms/widgets/KartenKnopf`).
 *
 * Quellenlage:
 * - **Google Earth**: das Kamerasegment `@lat,lng,…a,…d,…y,…h,…t,…r` ist von
 *   Google NICHT dokumentiert (Reverse Engineering aus Share-Links). Die
 *   Kameraparameter sind syntaktisch untrennbar von den Koordinaten — an
 *   `/web/search/<Adresse>` lässt sich keine Kamera anhängen. Ohne Koordinaten
 *   fällt der Builder deshalb exakt auf die bisherige Such-URL zurück.
 * - **TIM-online**: Parameter laut `tim-online.nrw.de/tim-online2/hilfe.html`.
 *   Weder `?address=` noch `?searchterm=` existiert (beides stand vorher im
 *   Code und war wirkungslos). Der Adressparameter heißt `adresse` im Format
 *   „Straße Hausnummer,Gemeinde" und ist NICHT mit anderen Parametern
 *   kombinierbar. Ein koordinatenbasierter `center`-Deeplink ist bewusst NICHT
 *   umgesetzt — Begründung an `buildTimOnlineUrl`.
 */

export type GeoCoords = {
  /** Breitengrad (WGS84, Dezimalgrad) */
  lat: number;
  /** Längengrad (WGS84, Dezimalgrad) */
  lng: number;
};

export type AddressParts = {
  street?: string | null;
  postalCode?: string | null;
  city?: string | null;
  country?: string | null;
};

// ── Google-Earth-Kamera ────────────────────────────────────────────────
// Alle sechs Werte des Kamerasegments stehen hier an EINER Stelle, damit sich
// die Schrägansicht ohne Suche im Code nachjustieren lässt.

/** `a` — Höhe des betrachteten Punkts in Metern. 0 = Meeresspiegel. Bewusste
 *  Ungenauigkeit: echte Share-URLs tragen hier die Geländehöhe; ohne
 *  Elevation-API (zusätzliches SKU) bleibt es bei 0. In Tieflagen unauffällig,
 *  in Höhenlagen sinkt der Fokuspunkt sichtbar unter das Gelände. */
const EARTH_TARGET_ALTITUDE_A = 0;
/** `d` — Abstand der Kamera zum Fokuspunkt in Metern. 300 m zeigt ein
 *  Einzelgebäude samt Grundstück und Nachbarbebauung. */
const EARTH_CAMERA_DISTANCE_D = 300;
/** `y` — vertikales Sichtfeld (field of view) in Grad. 35 entspricht dem
 *  Standardwert aus Earth-Share-Links. */
const EARTH_FIELD_OF_VIEW_Y = 35;
/** `h` — Kompassrichtung der Kamera in Grad. 0 = Blick nach Norden. */
const EARTH_HEADING_H = 0;
/** `t` — Neigung in Grad. 0 = senkrechte Draufsicht (heutiges Verhalten),
 *  90 = Blick auf den Horizont. 65 ist die gewünschte Schrägansicht: das
 *  Gebäude wird dreidimensional, der Himmel nimmt noch keinen Platz weg. */
const EARTH_TILT_T = 65;
/** `r` — Rollwinkel in Grad. 0 = Horizont waagerecht. */
const EARTH_ROLL_R = 0;

/** Nachkommastellen für Koordinaten in URLs (~1 cm — Google-Share-Links nutzen
 *  ebenfalls 7). */
const COORD_DECIMALS = 7;

const formatCoord = (value: number): string => value.toFixed(COORD_DECIMALS);

/**
 * Setzt die vier Adressfelder zu einer einzeiligen Anschrift zusammen —
 * exakt in der Form, die `AnschriftBlock` bisher inline gebaut hat
 * („Straße, PLZ Ort, Land"; leere Teile fallen samt Trenner weg).
 */
export function formatFullAddress({ street, postalCode, city, country }: AddressParts): string {
  return [
    (street ?? "").trim(),
    [(postalCode ?? "").trim(), (city ?? "").trim()].filter(Boolean).join(" "),
    (country ?? "").trim(),
  ]
    .filter(Boolean)
    .join(", ");
}

/**
 * Google-Earth-Link.
 *
 * - **mit Koordinaten**: geneigte 3D-Schrägansicht über das Kamerasegment.
 * - **ohne Koordinaten**: unverändert die bisherige Such-URL (byte-identisch) —
 *   das ist der Fallback, wenn kein Google-Key gesetzt, die Geocoding API nicht
 *   aktiviert oder der Treffer zu unpräzise ist.
 */
export function buildGoogleEarthUrl(fullAddress: string, coords?: GeoCoords | null): string {
  if (coords) {
    const segment = [
      `@${formatCoord(coords.lat)}`,
      formatCoord(coords.lng),
      `${EARTH_TARGET_ALTITUDE_A}a`,
      `${EARTH_CAMERA_DISTANCE_D}d`,
      `${EARTH_FIELD_OF_VIEW_Y}y`,
      `${EARTH_HEADING_H}h`,
      `${EARTH_TILT_T}t`,
      `${EARTH_ROLL_R}r`,
    ].join(",");
    return `https://earth.google.com/web/${segment}`;
  }
  return `https://earth.google.com/web/search/${encodeURIComponent(fullAddress)}`;
}

// ── TIM-online ─────────────────────────────────────────────────────────

/**
 * TIM-online-Link (Liegenschaftskarte NRW).
 *
 * Verlinkt wird bewusst NUR die Adressform `adresse=<Straße Hausnr>,<Gemeinde>`
 * — und zwar allein, denn der Adressparameter ist laut Hilfe nicht mit anderen
 * Parametern kombinierbar. Vorher stand hier `?address=` bzw. `?searchterm=`;
 * beide Parameter kennt TIM-online nicht, die Links waren wirkungslos.
 *
 * KEIN `center=`-Deeplink, obwohl hier Koordinaten vorliegen: das einzige
 * konkrete Beispiel aus `tim-online.nrw.de/tim-online2/hilfe.html`
 * (`center=381297,5771646`) ist ein ETRS89/UTM-32-Paar (Rechts-/Hochwert),
 * KEINE WGS84-Dezimalgrade. Ein `center` mit Längen-/Breitengrad landete damit
 * weit außerhalb NRWs und die Karte bliebe leer — der Deeplink wäre also
 * kaputter als der Adressparameter, den dieser PR gerade repariert. Für die
 * ALKIS-Ansicht per Koordinate braucht es erst eine Verifikation im Browser und
 * ggf. eine UTM-Umrechnung; das ist ein eigener Schritt.
 */
/**
 * TIM-online ist das Geoportal von NRW und kennt nur deutsche Anschriften.
 * Bei einer Immobilie im Ausland führte der Knopf ins Leere — er wird dort
 * nicht angeboten. Ein leeres Land gilt wie im übrigen Bestand als Deutschland.
 */
export function timOnlineMoeglich(parts: AddressParts): boolean {
  return deutscheAnschrift(parts);
}

/** Leeres Land gilt wie im übrigen Bestand als Deutschland. */
function deutscheAnschrift(parts: AddressParts): boolean {
  const land = (parts.country ?? "").trim().toLowerCase();
  return land === "" || land === "deutschland" || land === "de";
}

export function buildTimOnlineUrl(parts: AddressParts): string {
  const base = "https://www.tim-online.nrw.de/tim-online2/";
  const street = (parts.street ?? "").trim();
  const gemeinde = (parts.city ?? "").trim();
  const adresse = [street, gemeinde].filter(Boolean).join(",");
  return `${base}?adresse=${encodeURIComponent(adresse)}`;
}

// ── Hochwasser-Check (GDV) ─────────────────────────────────────────────

/**
 * Straße und Hausnummer getrennt — der Hochwasser-Check will sie in zwei
 * Feldern, im Bestand stehen sie in EINEM („Straße & Hausnummer“).
 *
 * Hausnummer ist der Zahlenblock am Ende samt Buchstabe und Bereich/Bruch
 * („12a“, „13 A“, „1-7“, „4/1“). Was keinen solchen Schluss hat, bleibt
 * vollständig Straße — eine falsch abgeschnittene Straße wäre schlimmer als
 * eine leere Hausnummer, die man auf der Seite noch eintippen kann.
 */
export function strasseUndHausnummer(street: string | null | undefined): { strasse: string; hausnummer: string } {
  const s = (street ?? "").replace(/\s+/g, " ").trim();
  const m = /^(.*[^\d\s,/–-])[\s,]*(\d+\s?[a-zA-Z]?(?:\s?[-–/]\s?\d+\s?[a-zA-Z]?)?)$/.exec(s);
  if (!m) return { strasse: s, hausnummer: "" };
  return { strasse: m[1].trim(), hausnummer: m[2].trim() };
}

const HOCHWASSER_CHECK_BASIS = "https://www.dieversicherer.de/versicherer/wohnen/hochwassercheck";

/**
 * Hochwasser-Check des GDV (T-339, Sascha Hahn 15.10.2026: „soll analog zu
 * Earth und TIM-online über das KMVS direkt möglich sein“).
 *
 * Die öffentliche Seite reicht `str`, `hnr`, `plz` und `ort` an das
 * eingebettete Werkzeug weiter und füllt damit die Adresse vor (im
 * Seitenquelltext als `forwardedParams`, geprüft am 28.10.2026). Nur
 * VERLINKEN, nicht einbetten — Einbetten erlaubt der GDV Maklern nicht.
 */
export function buildHochwasserCheckUrl(parts: AddressParts): string {
  const { strasse, hausnummer } = strasseUndHausnummer(parts.street);
  const params: Array<[string, string]> = [
    ["str", strasse],
    ["hnr", hausnummer],
    ["plz", (parts.postalCode ?? "").trim()],
    ["ort", (parts.city ?? "").trim()],
  ];
  const query = params
    .filter(([, wert]) => wert)
    .map(([name, wert]) => `${name}=${encodeURIComponent(wert)}`)
    .join("&");
  return query ? `${HOCHWASSER_CHECK_BASIS}?${query}` : HOCHWASSER_CHECK_BASIS;
}

/** Der Check deckt nur Deutschland ab — wie TIM-online. */
export function hochwasserCheckMoeglich(parts: AddressParts): boolean {
  return deutscheAnschrift(parts);
}

/**
 * Der Check liefert keine ZÜRS-Klasse. Steht das Ergebnis im letzten Drittel,
 * holt der Innendienst die ZÜRS-Abfrage ein (Vorgabe aus T-339).
 */
export const HOCHWASSER_HINWEIS =
  "Liegt das Ergebnis bei Hochwasser oder Starkregen im letzten Drittel, bitte die ZÜRS-Abfrage über Carsten einholen.";
